import React, { useState, useEffect, useCallback } from 'react';
import { useSelectedBrand } from '../hooks/useApi';
import LoadingSpinner from '../components/LoadingSpinner';
import PostModal from '../components/PostModal';
import apiService from '../services/api';

const PLATFORM_ICONS = {
  LINKEDIN: { icon: 'fab fa-linkedin', color: 'text-blue-700 bg-blue-50' },
  FACEBOOK: { icon: 'fab fa-facebook', color: 'text-blue-600 bg-blue-50' },
  INSTAGRAM: { icon: 'fab fa-instagram', color: 'text-pink-600 bg-pink-50' },
  X: { icon: 'fab fa-twitter', color: 'text-gray-900 bg-gray-100' },
};

const PlatformBadges = ({ platforms }) => {
  if (!platforms || platforms.length === 0) return null;
  return (
    <div className="flex items-center gap-1">
      {platforms.map(p => {
        const cfg = PLATFORM_ICONS[p] || { icon: 'fas fa-globe', color: 'text-gray-500 bg-gray-50' };
        return (
          <span key={p} className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs ${cfg.color}`} title={p}>
            <i className={cfg.icon}></i>
          </span>
        );
      })}
    </div>
  );
};

const Pipeline = () => {
  const { selectedBrandId, setSelectedBrandId, brands: brandOptions, brandsLoading } = useSelectedBrand();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingPost, setEditingPost] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const fetchPipeline = useCallback(async () => {
    if (!selectedBrandId) return;
    try {
      setLoading(true);
      const result = await apiService.getPipeline(selectedBrandId);
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedBrandId]);

  useEffect(() => { fetchPipeline(); }, [fetchPipeline]);

  const handleApprove = async (postId) => {
    setActionLoading(postId);
    try {
      await apiService.publishPost(postId);
      await fetchPipeline();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleSchedule = async (postId) => {
    setActionLoading(postId);
    try {
      // Schedule for next available slot (next Tue/Thu/Sat 7:30 AM EST)
      const now = new Date();
      const targetDays = [2, 4, 6]; // Tue, Thu, Sat
      let scheduled = new Date(now);
      for (let i = 1; i <= 14; i++) {
        scheduled = new Date(now.getTime() + i * 86400000);
        if (targetDays.includes(scheduled.getDay())) break;
      }
      scheduled.setHours(12, 30, 0, 0); // 7:30 AM EST = 12:30 UTC

      await apiService.updatePost(postId, {
        status: 'SCHEDULED',
        scheduled_time: scheduled.toISOString(),
      });
      await fetchPipeline();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (postId) => {
    setActionLoading(postId);
    try {
      await apiService.deletePost(postId);
      await fetchPipeline();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  if (brandsLoading) return <LoadingSpinner />;

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Content Pipeline</h1>
          <p className="text-sm text-gray-500 mt-1">Review and approve content from Forge</p>
        </div>
        {brandOptions.length > 1 && (
          <select
            value={selectedBrandId || ''}
            onChange={e => setSelectedBrandId(Number(e.target.value))}
            className="border border-gray-300 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500"
          >
            {brandOptions.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">dismiss</button>
        </div>
      )}

      {loading ? <LoadingSpinner /> : data && (
        <>
          {/* Status bar */}
          <div className="flex gap-4">
            {[
              { label: 'Drafts', count: data.counts.draft, color: 'text-amber-600 bg-amber-50' },
              { label: 'Scheduled', count: data.counts.scheduled, color: 'text-blue-600 bg-blue-50' },
              { label: 'Published', count: data.counts.published, color: 'text-green-600 bg-green-50' },
              { label: 'Failed', count: data.counts.failed, color: 'text-red-600 bg-red-50' },
            ].map(s => (
              <div key={s.label} className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium ${s.color}`}>
                <span>{s.count}</span>
                <span>{s.label}</span>
              </div>
            ))}
          </div>

          {/* Drafts (Review Queue) */}
          <div>
            <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <i className="fas fa-file-alt text-amber-500"></i>
              Drafts — {data.drafts.length} pending review
            </h2>
            {data.drafts.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center text-gray-400">
                <i className="fas fa-check-circle text-3xl mb-2"></i>
                <p>No drafts to review</p>
              </div>
            ) : (
              <div className="space-y-4">
                {data.drafts.map(post => (
                  <div key={post.id} className="bg-white rounded-2xl border border-gray-200 p-6">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        {post.post_type && (
                          <span className="px-2 py-0.5 bg-accent-50 text-accent-700 rounded text-xs font-medium">
                            {post.post_type}
                          </span>
                        )}
                        <PlatformBadges platforms={post.platforms} />
                        <span className="text-xs text-gray-400">
                          {new Date(post.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      {post.media_type !== 'none' && (
                        <span className="text-xs text-gray-400">
                          <i className="fas fa-paperclip mr-1"></i>{post.media_type}
                        </span>
                      )}
                    </div>

                    {(post.media_file || post.media_url) && post.media_type === 'image' && (
                      <div className="mb-3">
                        <img
                          src={post.media_file || post.media_url}
                          alt="Post attachment"
                          className="rounded-xl max-h-64 object-cover"
                        />
                      </div>
                    )}

                    {(post.media_file || post.media_url) && post.media_type === 'carousel_pdf' && (
                      <div className="mb-3 flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-xl text-sm text-gray-600">
                        <i className="fas fa-file-pdf text-red-500"></i>
                        <a href={post.media_file || post.media_url} target="_blank" rel="noopener noreferrer" className="underline">
                          View PDF carousel
                        </a>
                      </div>
                    )}

                    <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed mb-4">
                      {post.content}
                    </p>

                    <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
                      <button
                        onClick={() => handleSchedule(post.id)}
                        disabled={actionLoading === post.id}
                        className="px-4 py-2 bg-green-600 text-white rounded-xl text-sm font-medium hover:bg-green-700 disabled:opacity-50 transition-colors"
                      >
                        {actionLoading === post.id ? (
                          <i className="fas fa-spinner fa-spin mr-1"></i>
                        ) : (
                          <i className="fas fa-check mr-1"></i>
                        )}
                        Approve & Schedule
                      </button>
                      <button
                        onClick={() => setEditingPost(post)}
                        className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors"
                      >
                        <i className="fas fa-edit mr-1"></i>Edit
                      </button>
                      <button
                        onClick={() => handleReject(post.id)}
                        disabled={actionLoading === post.id}
                        className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-xl text-sm font-medium transition-colors"
                      >
                        <i className="fas fa-times mr-1"></i>Reject
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Scheduled */}
          <div>
            <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <i className="fas fa-clock text-blue-500"></i>
              Scheduled — {data.scheduled.length}
            </h2>
            {data.scheduled.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-6 text-center text-gray-400 text-sm">
                No scheduled posts
              </div>
            ) : (
              <div className="space-y-3">
                {data.scheduled.map(post => (
                  <div key={post.id} className="bg-white rounded-2xl border border-gray-200 p-4 flex items-center justify-between">
                    <div className="flex-1 min-w-0 mr-4">
                      <p className="text-sm text-gray-800 truncate">{post.content.slice(0, 120)}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {post.scheduled_time && new Date(post.scheduled_time).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <PlatformBadges platforms={post.platforms} />
                      {post.post_type && (
                        <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-medium">
                          {post.post_type}
                        </span>
                      )}
                      <button
                        onClick={() => handleApprove(post.id)}
                        disabled={actionLoading === post.id}
                        className="px-3 py-1.5 bg-green-600 text-white rounded-xl text-xs font-medium hover:bg-green-700 disabled:opacity-50 transition-colors"
                      >
                        {actionLoading === post.id ? (
                          <i className="fas fa-spinner fa-spin"></i>
                        ) : (
                          <><i className="fas fa-paper-plane mr-1"></i>Publish Now</>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Published this week */}
          <div className="bg-green-50 rounded-2xl border border-green-200 p-4 text-sm text-green-700">
            <i className="fas fa-check-circle mr-2"></i>
            {data.published_this_week} post{data.published_this_week !== 1 ? 's' : ''} published this week
          </div>
        </>
      )}

      {editingPost && (
        <PostModal
          isOpen={true}
          onClose={() => setEditingPost(null)}
          onSuccess={() => {
            setEditingPost(null);
            fetchPipeline();
          }}
          editPost={editingPost}
        />
      )}
    </div>
  );
};

export default Pipeline;
